/**
 * Responsibility: Build normalized ability-action UI payloads.
 * Scope: UI action-shaping only; ability resolution lives in game-store/forge layers.
 */
import type {
  Ability,
  ActionTargetRef,
  ActionToolRef,
  GameState,
  ModalAction,
} from "@anyquest/shared-core"
import { TOPICS } from "@anyquest/shared-core"
import { buildActionTarget, buildToolRef, toRefId } from './action-refs'

export function buildAbilityToolRef(ability: Ability | null | undefined): ActionToolRef {
  const label = String(ability?.name ?? 'Ability').trim() || 'Ability'
  return buildToolRef('ability', String(ability?.id ?? label), label)
}

function resolveTargetKind(gs: GameState, targetLabel: string): ActionTargetRef['kind'] {
  const wanted = String(targetLabel ?? '').trim().toLowerCase()
  const playerLabels = [
    String(gs.player?.name ?? '').trim().toLowerCase(),
    String(gs.player?.identity ?? '').trim().toLowerCase(),
  ].filter(Boolean)
  if (playerLabels.includes(wanted)) return 'player'
  if ((gs.companions ?? []).some(companion => companion.name.toLowerCase() === wanted)) return 'companion'
  return 'npc'
}

export function resolveAbilityTargets(gs: GameState | null | undefined, labels: string[] = []): ActionTargetRef[] {
  if (!gs) return []
  const refs = new Map<string, ActionTargetRef>()
  for (const raw of labels) {
    const label = String(raw ?? '').trim()
    if (!label) continue
    const kind = resolveTargetKind(gs, label)
    const ref: ActionTargetRef = kind === 'player'
      ? buildActionTarget('player', label, 'player')
      : buildActionTarget(kind, label, toRefId(label) || kind)
    refs.set(`${ref.kind}:${ref.id}`, ref)
  }
  return [...refs.values()]
}

type AbilityModalActionOptions = {
  resolvedArgs?: Ability['args']
  targets?: string[]
  title?: string
  contextTag?: string
  preferredTargetName?: string
}

export function buildAbilityModalActions(
  gs: GameState,
  ability: Ability,
  options: AbilityModalActionOptions = {},
): ModalAction[] {
  const tool = buildAbilityToolRef(ability)
  const contextTag = String(options.contextTag ?? '').trim()
  const baseTags = ['ability_modal_action']
  if (contextTag) baseTags.push(contextTag)
  const resolvedArgs = Array.isArray(options.resolvedArgs) ? options.resolvedArgs : []

  if (resolvedArgs.length) {
    return [{
      label: 'USE',
      topic: TOPICS.ui.dialogOpen,
      topicData: {
        content: 'ability-args-dialog',
        data: {
          ability: { ...ability, args: resolvedArgs },
          targets: Array.isArray(options.targets) ? options.targets : [],
        },
        options: {
          title: String(options.title ?? ability.name ?? 'Ability').trim() || 'Ability',
        },
      },
      input: `Use ability: ${ability.name}`,
      source: 'player',
      mode: 'do',
      category: 'ability',
      tool,
      tags: [...baseTags, 'ability_args_required'],
    }]
  }

  const explicitTargets = options.preferredTargetName
    ? resolveAbilityTargets(gs, [options.preferredTargetName])
    : []

  return [{
    label: 'USE',
    input: `Use ability: ${ability.name}`,
    source: 'player',
    mode: 'do',
    category: 'ability',
    ...(explicitTargets.length ? { explicitTargets } : {}),
    tool,
    tags: [...baseTags, 'ability_execute'],
  }]
}
